import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import MediaComponent from '../Media/media-component.js';
import { getAllCategories } from '../../Public/category-data';
import DataSourceConfig from '../../Config/DataSourceConfig';

// MaestroShield design-system palette.
const NAVY = '#0E1A2B';
const GOLD = '#FFC629';
const SOFT_WHITE = '#F7F8FA';
const INK = '#1F2937';

const DRAG_THRESHOLD = 60;
const PLACEHOLDER = '/placeholder.svg';

/**
 * Category "Our Solutions" carousel, image cards with a navy gradient scrim and
 * text overlaid at the bottom. Shows 2 cards side by side on desktop (1 on
 * mobile) and pages through the rest. Each card links to the category's first
 * service when one exists, otherwise to the services grid (never a dead slug).
 */
export default function CategoryBanners({ headingLine1 = 'Our', headingLine2 = 'Solutions' }) {
    const { dir = 'ltr', language = 'en', categories: backendCats } = useSelector((state) => state.systemData || {});

    // Source: S3 categories when wired to the backend, else the local mocks.
    // When backend-driven, DON'T fall back to local mocks while loading, that
    // would render placeholder cards and then swap to the real ones (flicker /
    // layout shift on load). Render nothing until the real categories arrive.
    const cards = useMemo(() => {
        const source = DataSourceConfig.categories === 'backend'
            ? (Array.isArray(backendCats) ? backendCats : [])
            : getAllCategories(language);
        return (source || [])
            .filter(Boolean)
            .map((c) => ({
                title: c.title || c.name || '',
                description: c.description || c.subtitle || '',
                image: c.image || c.media || PLACEHOLDER,
                ctaText: c.ctaText || 'Read More',
                // Each card links to its own category page (resolves by slug/id).
                href: c.slug ? `/categories/${c.slug}` : '/categories',
            }));
    }, [backendCats, language]);

    const total = cards.length;

    // Cards per view: 2 on desktop, 1 on mobile. Seed from the real viewport on
    // first render so mobile doesn't briefly paint the 2-up desktop layout.
    const [perView, setPerView] = useState(
        () => (typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches) ? 2 : 1
    );
    useEffect(() => {
        const mq = window.matchMedia('(min-width: 768px)');
        const apply = () => setPerView(mq.matches ? 2 : 1);
        apply();
        mq.addEventListener('change', apply);
        return () => mq.removeEventListener('change', apply);
    }, []);

    // Visible cards (capped at how many exist) and whether to loop at all.
    // Only loop when there are MORE cards than fit on screen, otherwise every
    // card is already visible and "looping" just shuffles them (renders badly).
    const pv = Math.max(1, Math.min(perView, total));
    const loop = total > pv;
    const cellWidth = 100 / pv;                   // each card cell, % of viewport

    // Clone `pv` cards on each side so dragging/paging past either end wraps
    // seamlessly, this is what lets 1-2 categories loop instead of dead-ending.
    const extended = loop ? [...cards.slice(total - pv), ...cards, ...cards.slice(0, pv)] : cards;
    const BASE = loop ? pv : 0;                   // real card 0 lives at extended[pv]

    const [index, setIndex] = useState(0);        // logical index; briefly hits -1 or `total` mid-wrap
    const [noAnim, setNoAnim] = useState(false);  // disable transition during the seamless jump
    const [dragPx, setDragPx] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [paused, setPaused] = useState(false);
    const startX = useRef(null);
    const vpRef = useRef(null);

    // Reset when the visible-count or card-count changes.
    useEffect(() => { setIndex(0); }, [pv, total]);

    const prev = useCallback(() => { if (loop) setIndex((i) => i - 1); }, [loop]);
    const next = useCallback(() => { if (loop) setIndex((i) => i + 1); }, [loop]);

    // After sliding onto a clone, snap (without animation) back to the real card.
    // Only react to the track's OWN transform transition, not the many child
    // transitions (buttons, hovers) that bubble up and would cause false resets.
    const onTransitionEnd = (e) => {
        if (!loop || e.target !== e.currentTarget || e.propertyName !== 'transform') return;
        if (index >= total) { setNoAnim(true); setIndex((i) => i - total); }
        else if (index < 0) { setNoAnim(true); setIndex((i) => i + total); }
    };
    useEffect(() => {
        if (!noAnim) return;
        const r = requestAnimationFrame(() => setNoAnim(false));
        return () => cancelAnimationFrame(r);
    }, [noAnim]);

    useEffect(() => {
        if (paused || !loop) return;
        const id = setInterval(() => next(), 5000);
        return () => clearInterval(id);
    }, [paused, loop, next]);

    const onDown = (e) => { setPaused(true); startX.current = e.clientX; setIsDragging(true); };
    const onMove = (e) => { if (startX.current !== null) setDragPx(e.clientX - startX.current); };
    const finish = (clientX) => {
        if (startX.current === null) return;
        const delta = clientX - startX.current;
        startX.current = null; setIsDragging(false); setDragPx(0);
        if (Math.abs(delta) > DRAG_THRESHOLD) (delta < 0 ? next() : prev());
    };

    if (!total) return null;

    const vpWidth = vpRef.current?.offsetWidth || 1;
    const translate = -((index + BASE) * cellWidth) + (dragPx / vpWidth) * 100;
    const activeDot = ((index % total) + total) % total;

    return (
        <section dir={dir} className="w-full bg-white pt-4 md:pt-6 pb-14 md:pb-20 px-4 md:px-8">
            <div className="mx-auto max-w-[1200px]">
                {/* Header */}
                <div className="mb-6 md:mb-8">
                    <h2 className="font-secondary text-3xl sm:text-4xl md:text-5xl font-extrabold leading-[0.95] tracking-[-0.02em]" style={{ color: NAVY }}>
                        <span className="inline">{headingLine1} </span>
                        <span className="inline" style={{ color: GOLD }}>{headingLine2}</span>
                    </h2>
                </div>

                {/* Viewport */}
                <div
                    ref={vpRef}
                    dir="ltr"
                    className="relative w-full overflow-hidden cursor-grab active:cursor-grabbing select-none"
                    style={{ touchAction: 'pan-y' }}
                    onPointerDown={onDown}
                    onPointerMove={onMove}
                    onPointerUp={(e) => finish(e.clientX)}
                    onPointerLeave={(e) => finish(e.clientX)}
                    onDragStart={(e) => e.preventDefault()}
                >
                    <div
                        className="flex"
                        style={{
                            transform: `translateX(${translate}%)`,
                            transition: (isDragging || noAnim) ? 'none' : 'transform 0.6s cubic-bezier(0.22,1,0.36,1)',
                        }}
                        onTransitionEnd={onTransitionEnd}
                    >
                        {extended.map((card, i) => {
                            // Real card index (so alternating colors stay consistent through clones).
                            const realIdx = loop ? ((((i - BASE) % total) + total) % total) : i;
                            const navy = realIdx % 2 === 0;
                            return (
                            <div key={i} className="shrink-0 px-2 md:px-3" style={{ flex: `0 0 ${cellWidth}%` }}>
                                {/* ---------- Mobile: full-bleed image card with gradient scrim + bottom text ---------- */}
                                <article className="relative aspect-[4/5] overflow-hidden rounded-[24px] md:hidden">
                                    <MediaComponent
                                        src={card.image}
                                        alt={card.title}
                                        objectFit="cover"
                                        useSekeleton={false}
                                        eager
                                        quality={65}
                                        wrapperClassName="absolute inset-0 h-full w-full [&_img]:pointer-events-none [&_img]:select-none"
                                    />
                                    <div
                                        className="pointer-events-none absolute inset-0"
                                        style={{ background: `linear-gradient(to top, ${NAVY} 6%, rgba(14,26,43,0.82) 36%, rgba(14,26,43,0.12) 68%, rgba(14,26,43,0.02) 100%)` }}
                                    />
                                    {/* White circle overlay, centered on the image (mobile) */}
                                    <div className="pointer-events-none absolute left-1/2 top-[34%] aspect-square h-[56%] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white" />
                                    <div className="absolute inset-x-0 bottom-0 flex flex-col gap-3 p-6 text-white">
                                        <h3 className="font-secondary text-[26px] font-extrabold leading-[1.08]">{card.title}</h3>
                                        {card.description && (
                                            <p className="text-[15px] leading-relaxed text-white/80 line-clamp-2">{card.description}</p>
                                        )}
                                        <a href={card.href}
                                            className="mt-1 inline-flex w-fit items-center gap-2 rounded-full px-6 py-3 text-[15px] font-bold transition-all active:scale-[0.98]"
                                            style={{ backgroundColor: GOLD, color: NAVY }}>
                                            {card.ctaText}
                                            <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                                        </a>
                                    </div>
                                </article>

                                {/* ---------- Desktop: split card, alternating navy / soft-white text panel + image with circle ---------- */}
                                <article className="hidden aspect-[16/10] grid-cols-2 overflow-hidden rounded-[24px] md:grid">
                                    {/* Text panel */}
                                    <div className="flex flex-col justify-center gap-4 p-8 lg:p-10"
                                        style={{ backgroundColor: navy ? NAVY : SOFT_WHITE, color: navy ? '#FFFFFF' : INK }}>
                                        <h3 className="font-secondary text-2xl lg:text-[28px] font-extrabold leading-[1.1]">{card.title}</h3>
                                        {card.description && (
                                            <p className="text-sm lg:text-[15px] leading-relaxed line-clamp-4" style={{ color: navy ? 'rgba(255,255,255,0.75)' : 'rgba(31,41,55,0.72)' }}>
                                                {card.description}
                                            </p>
                                        )}
                                        <div className="pt-1">
                                            <a href={card.href}
                                                className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-[15px] font-bold transition-all active:scale-[0.98]"
                                                style={navy
                                                    ? { border: '2px solid #FFFFFF', color: '#FFFFFF' }
                                                    : { backgroundColor: GOLD, color: NAVY }}>
                                                {card.ctaText}
                                                <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                                            </a>
                                        </div>
                                    </div>
                                    {/* Image panel with centered circle overlay */}
                                    <div className="relative overflow-hidden" style={{ backgroundColor: navy ? NAVY : SOFT_WHITE }}>
                                        <MediaComponent
                                            src={card.image}
                                            alt={card.title}
                                            objectFit="cover"
                                            useSekeleton={false}
                                            eager
                                            quality={65}
                                            wrapperClassName="absolute inset-0 h-full w-full [&_img]:pointer-events-none [&_img]:select-none"
                                        />
                                        <div className="pointer-events-none absolute left-1/2 top-1/2 aspect-square h-[60%] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white/70" />
                                    </div>
                                </article>
                            </div>
                            );
                        })}
                    </div>

                    {/* Side navigation arrows */}
                    {loop && (
                        <>
                            <button type="button" onClick={prev} aria-label="Previous"
                                className="absolute left-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border-2 bg-white/90 shadow-lg backdrop-blur transition-colors hover:bg-[#FFC629] md:left-4 md:h-12 md:w-12"
                                style={{ borderColor: GOLD, color: NAVY }}>
                                <ChevronLeft className="h-5 w-5 md:h-6 md:w-6" strokeWidth={2.5} />
                            </button>
                            <button type="button" onClick={next} aria-label="Next"
                                className="absolute right-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border-2 bg-white/90 shadow-lg backdrop-blur transition-colors hover:bg-[#FFC629] md:right-4 md:h-12 md:w-12"
                                style={{ borderColor: GOLD, color: NAVY }}>
                                <ChevronRight className="h-5 w-5 md:h-6 md:w-6" strokeWidth={2.5} />
                            </button>
                        </>
                    )}
                </div>

                {/* Dots, one per card */}
                {loop && (
                    <div className="mt-6 flex justify-center gap-2.5">
                        {cards.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => { setPaused(true); setIndex(i); }}
                                aria-label={`Go to slide ${i + 1}`}
                                className="h-2.5 rounded-full transition-all"
                                style={{ width: i === activeDot ? 26 : 10, backgroundColor: i === activeDot ? GOLD : '#D6DAE1' }}
                            />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
